import { lookupCard } from "../../content/index.js";
import { getCase } from "../../content/lookups.js";
import { workerHeal } from "../deck/workerHeal.js";
import { workerProgress } from "../deck/workerProgress.js";
import type { ActiveCase, CardLookup, CaseDefinition, ResolutionEvent, RunState } from "../types/index.js";
import {
  FAILURE_MESSAGE,
  GAMEOVER_MESSAGE,
  OVERDUE_MESSAGE,
  SUCCESS_MESSAGE,
  formatDeadlineMessage,
  formatHealMessage,
  formatLoadMessage,
  formatProgressMessage,
} from "./messages.js";
import { fatigueLimit } from "../run/fatigueLimit.js";
import { NEGOTIATION_ORG } from "../run/orgIds.js";
import { loadProgressDelta } from "./loadProgressDelta.js";
import { pickLoad } from "./pickLoad.js";

export type PlanEventsInput = {
  run: RunState;
  activeCase: ActiveCase;
  lookup?: CardLookup;
};

export type PlanEventsResult = {
  events: ResolutionEvent[];
  rngState: number;
};

function progressEvent(
  gain: number,
): ResolutionEvent | null {
  if (gain <= 0) return null;
  return {
    kind: "progress",
    gain,
    message: formatProgressMessage(gain),
  };
}

function healEvent(
  run: RunState,
  lookup: CardLookup,
): Extract<ResolutionEvent, { kind: "heal" }> | null {
  const amount = Math.min(
    run.fatigue,
    workerHeal({ hand: run.hand, lookup }),
  );
  if (amount <= 0) return null;
  return { kind: "heal", amount, message: formatHealMessage(amount, run.hand, lookup) };
}

function loadEvent(
  load: ReturnType<typeof pickLoad>["load"],
  afterProgress: number,
  block: number,
  negotiate: boolean,
): ResolutionEvent {
  const raw = load.fatigue ?? 0;
  const fatigueDealt = Math.max(0, raw - block);
  const progressDelta = loadProgressDelta(load.progress ?? 0, block, negotiate);
  const toProgress = progressDelta
    ? Math.max(0, afterProgress + progressDelta)
    : afterProgress;

  return {
    kind: "load",
    loadId: load.id,
    loadName: load.name,
    fatigueDealt,
    fatigueRaw: raw,
    blockUsed: block,
    progressDelta,
    nextEnergyModifier: load.nextEnergyModifier ?? 0,
    nextHandDiscard: load.nextHandDiscard,
    addTemporaryCards: load.addTemporaryCards,
    nextActionDrawLimit: load.nextActionDrawLimit,
    message: formatLoadMessage(load),
  };
}

function discardEvent(run: RunState): ResolutionEvent | null {
  const amount = run.hand.filter((card) => !lookupCard(card.cardId)?.retainedFatigue).length + run.inPlay.length + run.aside.length;
  if (amount === 0) return null;
  return { kind: "discard", amount, message: run.hand.some((card) => lookupCard(card.cardId)?.retainedFatigue)
    ? "対応事項を手札に残し、それ以外のカードを捨て札にした。"
    : "このラウンドのカードを全て捨て札にした。" };
}

function afterLoadEvents(
  run: RunState,
  activeCase: ActiveCase,
  afterProgress: number,
  fatigueAfterLoad: number,
  caseDef: CaseDefinition,
): ResolutionEvent[] {
  if (fatigueAfterLoad >= fatigueLimit(run.organizations)) {
    return [{ kind: "gameover", message: GAMEOVER_MESSAGE }];
  }

  const from = activeCase.deadline;
  const to = from - 1;
  const events: ResolutionEvent[] = [
    {
      kind: "deadline",
      from,
      to,
      message: formatDeadlineMessage(from, to),
    },
  ];

  if (to > 0) return events;

  events.push({ kind: "overdue", message: OVERDUE_MESSAGE });
  const fatigueAfterOverdue = fatigueAfterLoad + 5;

  if (fatigueAfterOverdue >= fatigueLimit(run.organizations) || caseDef.isBoss) {
    events.push({ kind: "gameover", message: GAMEOVER_MESSAGE });
  } else {
    events.push({ kind: "failure", message: FAILURE_MESSAGE });
  }

  return events;
}

export function planEvents(input: PlanEventsInput): PlanEventsResult {
  const lookup = input.lookup ?? lookupCard;
  const { run, activeCase } = input;
  const caseDef = getCase(activeCase.caseId);
  if (!caseDef) return { events: [], rngState: run.rngState };
  const workerGain = activeCase.workerProgressDisabled ? 0 : workerProgress({
    hand: run.hand,
    organizations: run.organizations,
    lookup,
    noActionsPlayed: run.inPlay.length === 0,
  });
  const actionGain = activeCase.pendingProgress ?? 0;
  const gain = workerGain + actionGain;
  const afterProgress = activeCase.progress + gain;
  const events: ResolutionEvent[] = [];

  const prog = progressEvent(gain);
  if (prog) events.push(prog);
  const heal = healEvent(run, lookup);
  if (heal) events.push(heal);

  // ハプニング中央表示が場のカードに被らないよう、捨て札を先に処理する。
  const discard = discardEvent(run);
  if (discard) events.push(discard);

  if (afterProgress >= (activeCase.required ?? caseDef.required)) {
    events.push({ kind: "success", message: SUCCESS_MESSAGE });
    return { events, rngState: run.rngState };
  }

  // 対応事項は手札にある間だけ発生。新たに送り込まれた札はこの回には数えない。
  const taskFatigue = run.hand.reduce((sum, card) => sum + (lookup(card.cardId)?.retainedFatigue ?? 0), 0);
  const taskBlock = Math.min(run.block, taskFatigue);
  const taskDamage = taskFatigue - taskBlock;
  if (taskFatigue > 0) {
    events.push({ kind: "load", loadId: "retained-tasks", loadName: "未処理の対応事項",
      fatigueRaw: taskFatigue, fatigueDealt: taskDamage, blockUsed: taskBlock,
      progressDelta: 0, nextEnergyModifier: 0,
      message: `手札に残った対応事項により疲労+${taskDamage}。${taskBlock > 0 ? `防御で${taskBlock}軽減した。` : ""}処理するか、手札から外すまで続く。`,
    });
    if (run.fatigue - (heal?.amount ?? 0) + taskDamage >= fatigueLimit(run.organizations)) {
      events.push({ kind: "gameover", message: GAMEOVER_MESSAGE });
      return { events, rngState: run.rngState };
    }
  }
  const picked = pickLoad({ loads: caseDef.loads, rngState: run.rngState });
  const loadEventResult = loadEvent(
    picked.load,
    afterProgress,
    run.block - taskBlock,
    run.organizations.includes(NEGOTIATION_ORG),
  );
  const fatigueAfterHeal = run.fatigue - (heal?.amount ?? 0) + taskDamage;
  const fatigueAfterLoad =
    fatigueAfterHeal + (loadEventResult.kind === "load" ? loadEventResult.fatigueDealt : 0);
  events.push(loadEventResult);
  events.push(
    ...afterLoadEvents(run, activeCase, afterProgress, fatigueAfterLoad, caseDef),
  );

  return { events, rngState: picked.rngState };
}
